import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { AppState } from '../../client-redux/reducers';
import { Language } from '../../languages/languageHandler';
import { IAPMPComissions, IAPMPeriods, fetchComissionsData } from '../../../libraries/apm';
import ModalComponent from '../modal';
import { Loader, Table, Modal } from '@mantine/core';
import { useStatusOperDialog, isZarphSuccessResponse } from '../../context/status-oper-dialog';
import { IconListDetails } from '@tabler/icons-react';
import './styles.scss';

interface ShowComissionsProps {
  a001: number;
  movType: string;
  a031: number;
  a032: number;
  a029: number;
  dateTime: string;
}

const ShowComissionsDetails = ({ a001, movType, a031, a032, a029, dateTime }: ShowComissionsProps) => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);

  const controllerRef = useRef(new AbortController());

  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const [listComissions, setListComissions] = useState<IAPMPComissions[]>([]);
  const [listPeriods, setListPeriods] = useState<IAPMPeriods[]>([]);

  const { handleApiError } = useStatusOperDialog();

  useEffect(() => {
    if (showModal) {
      getComissionDetails();
    }

    return () => {
      controllerRef.current.abort();
      controllerRef.current = new AbortController();
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  const getComissionDetails = () => {
    setLoading(true);
    setListComissions([]);
    setListPeriods([]);

    fetchComissionsData(a001, movType, a031, a032, a029, dateTime, controllerRef.current)
      .then(response => {
        if (isZarphSuccessResponse(response, [9004])) {
          const data = response.data;

          setListComissions(data.commissions || []);
          setListPeriods(data.periods || []);
        } else {
          handleApiError(response, lang.ALERT_ERROR_FETCHING_DATA);
        }
      })
      .catch(error => {
        handleApiError(error, lang.ALERT_ERROR_FETCHING_DATA);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const openModal = () => setShowModal(true);
  const closeModal = () => setShowModal(false);

  const header = (
    <div>
      <Modal.Title>{lang.GLOBAL_COMISSIONS}</Modal.Title>

      <div className="header-details">
        {listComissions.length > 0 ? (
          <div>
            <span>{listComissions[0]?.key.a001} / </span>
            <span>{listComissions[0]?.key.a031}</span>
          </div>
        ) : listPeriods.length > 0 ? (
          <div>
            <span>{listPeriods[0]?.key.a001} / </span>
            <span>{listPeriods[0]?.key.a031}</span>
          </div>
        ) : null}
      </div>
    </div>
  );

  return (
    <>
      <IconListDetails stroke={2} onClick={openModal} />

      <ModalComponent
        show={showModal}
        handleClick={closeModal}
        title={header}
        content={
          <Modal.Body>
            <Table.ScrollContainer minWidth={500}>
              <Table className="comissions-table" withTableBorder withColumnBorders>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Td>POS</Table.Td>
                    <Table.Td>{lang.GLOBAL_PERIOD}</Table.Td>
                    <Table.Td>{lang.GLOBAL_CONTRACT}</Table.Td>
                    <Table.Td>{lang.TRANSACTION_NUMBERS}</Table.Td>
                    <Table.Td>{lang.TRANSACTION_AMOUNT_CHARGED}</Table.Td>
                    <Table.Td>{lang.TRANSACTION_COMISSION_AMOUNT}</Table.Td>
                    <Table.Td>{lang.GLOBAL_COMISSIONS}</Table.Td>
                  </Table.Tr>
                </Table.Thead>

                <Table.Tbody>
                  {loading ? (
                    <Table.Tr>
                      <Table.Td colSpan={7}>
                        <Loader color="#016273" />
                      </Table.Td>
                    </Table.Tr>
                  ) : !listComissions.length || !listPeriods.length ? (
                    <Table.Tr>
                      <Table.Td colSpan={7} style={{ textAlign: 'center' }}>
                        {lang.GLOBAL_NO_RESULTS}
                      </Table.Td>
                    </Table.Tr>
                  ) : (
                    listComissions.map((commission, index) => (
                      <Table.Tr key={index}>
                        <Table.Td>{commission.key.a001}</Table.Td>
                        <Table.Td>{commission.key.a031}</Table.Td>
                        <Table.Td>{commission.a054}</Table.Td>
                        <Table.Td>{commission.a052}</Table.Td>
                        <Table.Td>{(commission.amount / 100).toFixed(2)} €</Table.Td>
                        <Table.Td>{(commission.amountCommission / 100).toFixed(2)} €</Table.Td>
                        <Table.Td>{commission.pCommission}</Table.Td>
                      </Table.Tr>
                    ))
                  )}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Modal.Body>
        }
      />
    </>
  );
};

export default ShowComissionsDetails;
